/* eslint-disable @typescript-eslint/no-floating-promises */
import type RouterService from "@ember/routing/router-service";
import Service, { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { ChartOptions, TimeScaleOptions } from "chart.js";
import dayjs from "dayjs";
import { task } from "ember-concurrency";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type ConsultationPlannedJournalEvent from "parog-web/models/consultation-planned-journal-event";
import type ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type DrugsAndDevicesRefundedJournalEvent from "parog-web/models/drugs-and-devices-refunded-journal-event";
import JournalEventModel from "parog-web/models/journal-event";
import type MedicalEventJournalEvent from "parog-web/models/medical-event-journal-event";
import VitalSign from "parog-web/models/vital-sign";
import type FetcherService from "parog-web/services/fetcher";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
export type Timeframe = "year" | "trimester" | "month";

const jesToFetch: string[] = [
  "BioExamExpectedDosageChangedJournalEvent",
  "BiologicalActRefundedJournalEvent",
  "HeightMeasuredJournalEvent",
  "MedicalActRefundedJournalEvent",
  "MedicalImagingRefundedJournalEvent",
  "TreatmentHistoryChangedJournalEvent"
];

export default class DashboardService extends Service {
  @service declare fetcher: FetcherService;
  @service declare router: RouterService;

  @tracked private _endDate: Date = this.today;
  @tracked private _timeframe: Timeframe = "trimester";

  get chartOptions(): ChartOptions<"line"> {
    return {
      clip: false, // See https://github.com/chartjs/Chart.js/issues/9372
      scales: {
        x: {
          type: "time",
          grid: { display: false },
          time: {
            displayFormats: {
              week: "DD MMM",
              month: "MMM YY",
              day: "DD MMM"
            },
            unit: this.scaleXTimeUnit
          },
          min: +this.timeframeStartDate,
          max: +this.timeframeEndDate,
          ticks: { maxTicksLimit: 8 }
        },
        y: { display: false }
      },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      layout: {
        padding: {
          top: 4,
          bottom: 4
        }
      }
    };
  }

  set endDate(endDate: Date) {
    this._endDate = endDate;
    this.fetchGraphDataTask.perform();
  }

  get endDate(): Date {
    return this._endDate;
  }

  get patientId(): string | undefined {
    return this.router.currentURL?.match(/\/patients\/(\d+)/)?.[1];
  }

  get scaleXTimeUnit(): TimeScaleOptions["time"]["unit"] {
    switch (this.timeframe) {
      case "month":
        return "day";
      case "trimester":
        return "week";
      case "year":
      default:
        return "month";
    }
  }

  set timeframe(timeframe: Timeframe) {
    this._timeframe = timeframe;
    this.fetchGraphDataTask.perform();
  }

  get timeframe(): Timeframe {
    return this._timeframe;
  }

  // Return the end date, at the end of the day
  get timeframeEndDate(): Date {
    return dayjs(this.endDate).endOf("day")?.toDate();
  }

  get timeframeStartDate(): Date {
    const date = new Date(this.endDate);
    let weeksAgo;

    if (this.timeframe === "month") weeksAgo = 6;
    else if (this.timeframe === "trimester") weeksAgo = 14;
    else if (this.timeframe === "year") weeksAgo = 52; // 1 year == 52 weeks

    // Go back X weeks ago
    date.setDate(date.getDate() - weeksAgo * 7);

    // Go back at the start of the month if we are using the "year" timeframe
    if (this.timeframe === "year") date.setDate(1);
    return date;
  }

  get today(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  addOneMonthToDate = () => {
    this.endDate = dayjs(this.endDate).add(6, "weeks").toDate();
  };

  fetchGraphData(
    patientId
  ): Promise<
    [
      JournalEventModel[],
      any,
      ConsultationPlannedJournalEvent[],
      DrugsAndDevicesRefundedJournalEvent[],
      ConsultationReportWrittenJournalEvent[],
      BioExamResultsReceivedJournalEvent[],
      MedicalEventJournalEvent[]
    ]
  > {
    if (!patientId) {
      throw `Missing patient id for url: ${this.router.currentURL}`;
    }
    const vitalSignsJes = VitalSign.allVitalSignsJETypes();
    const patientJesReq = JournalEventModel.patientJournalEventsRequest({
      fetcher: this.fetcher,
      patientId: patientId,
      jeTypesToFetch: [...new Set(jesToFetch.concat(vitalSignsJes))],
      jeTypesToIgnore: ["BloodPressureMeasuredJournalEvent"],
      occurredAfter: this.timeframeStartDate,
      occurredBefore: this.endDate
    });

    const bloodPressureJesReq = this.fetcher.loadRecords(
      "blood-pressure-session-started-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate)
        },
        include: "blood-pressure-measured-journal-events"
      }
    );

    const consultationPlannedJesReq = this.fetcher.loadRecords(
      "consultation-planned-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate),
          occurred_at_lte: formatToISO8601Date(this.endDate)
        },
        include: "creator"
      }
    );

    const drugsAndDevicesRefundedJesReq = this.fetcher.loadRecords(
      "drugs-and-devices-refunded-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate)
        },
        include:
          "medication-dispenses.medication-presentation.medication-specialty.medication-molecule"
      }
    );

    const consultationReportWrittenJesReq = this.fetcher.loadRecords(
      "consultation-report-written-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate),
          occurred_at_lte: formatToISO8601Date(this.endDate)
        },
        include: "creator,file-attachments"
      }
    );

    const bioExamResultsReceivedJesReq = this.fetcher.loadRecords(
      "bio-exam-results-received-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate)
        },
        include: "creator,file-attachments,bio-exam-results"
      }
    );

    const medicalEventJesReq = this.fetcher.loadRecords(
      "medical-event-journal-event",
      {
        filter: {
          patient_id: patientId,
          occurred_at_gte: formatToISO8601Date(this.timeframeStartDate),
          occurred_at_lte: formatToISO8601Date(this.endDate)
        },
        include: "creator,file-attachments"
      }
    );

    return Promise.all([
      patientJesReq,
      bloodPressureJesReq,
      consultationPlannedJesReq,
      drugsAndDevicesRefundedJesReq,
      consultationReportWrittenJesReq,
      bioExamResultsReceivedJesReq,
      medicalEventJesReq
    ]);
  }

  fetchGraphDataTask = task({ restartable: true }, async () => {
    await this.fetchGraphData(this.patientId);
  });

  reset() {
    this._timeframe = "trimester";
    this._endDate = this.today;
  }

  resetTime = () => {
    this.endDate = this.today;
  };

  substractOneMonthToDate = () => {
    this.endDate = dayjs(this.endDate).subtract(6, "weeks").toDate();
  };
}

declare module "@ember/service" {
  interface Registry {
    dashboard: DashboardService;
  }
}
